/** @file blitImage: copies or source-over blends one image onto another, clipped to the destination. */
import type { Image } from '../types/image';

/** Straight-alpha source-over of the pixel at `s` in `src` onto the pixel at `d` in `dst`, integer arithmetic. */
function over(dst: Uint8ClampedArray, d: number, src: Uint8ClampedArray, s: number): void {
  const sa = src[s + 3] ?? 0;
  const da = dst[d + 3] ?? 0;
  const keep = (da * (255 - sa)) / 255;
  const outA = sa + keep;
  for (let c = 0; c < 3; c++) {
    dst[d + c] = outA === 0 ? 0 : Math.round(((src[s + c] ?? 0) * sa + (dst[d + c] ?? 0) * keep) / outA);
  }
  dst[d + 3] = Math.round(outA);
}

/** Draws `src` with its top-left at (`x`, `y`) of `dst`; `'copy'` (default) overwrites, `'over'` blends straight alpha. */
export function blitImage(dst: Image, src: Image, x: number, y: number, mode: 'copy' | 'over' = 'copy'): void {
  const x0 = Math.max(0, x);
  const y0 = Math.max(0, y);
  const x1 = Math.min(dst.width, x + src.width);
  const y1 = Math.min(dst.height, y + src.height);
  if (x1 <= x0) return;
  for (let row = y0; row < y1; row++) {
    const s = ((row - y) * src.width + (x0 - x)) * 4;
    const d = (row * dst.width + x0) * 4;
    if (mode === 'copy') {
      dst.data.set(src.data.subarray(s, s + (x1 - x0) * 4), d);
      continue;
    }
    for (let i = 0; i < (x1 - x0) * 4; i += 4) over(dst.data, d + i, src.data, s + i);
  }
}
